// Sampler tab — shows the aggregate sampler score on the current 2D slice
// as a diverging heatmap, with the top-K candidate points overlaid as
// numbered diamonds. A left sidebar lets the user exclude individual outputs
// from the blended score by toggling them off.

import { useMemo, useState } from 'react';
import Plot from 'react-plotly.js';
import {
  Box,
  Checkbox,
  Flex,
  Heading,
  Text,
} from '@radix-ui/themes';

import { useCampaignStateQuery, useScoreQuery } from '../api';
import { useSliceState } from '../surfaces';

export type SamplerTabProps = {
  campaignId: string;
};

export function SamplerTab({ campaignId }: SamplerTabProps) {
  const stateQuery = useCampaignStateQuery(campaignId);
  const state = stateQuery.data;

  const inputs = state?.config.inputs ?? [];
  const outputs = state?.config.outputs ?? [];
  const { slice } = useSliceState(campaignId, inputs);

  const [disabledOutputs, setDisabledOutputs] = useState<Set<string>>(
    () => new Set(),
  );

  const toggleOutput = (name: string, enabled: boolean) => {
    setDisabledOutputs((prev) => {
      const next = new Set(prev);
      if (enabled) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const scoreParams = useMemo(
    () => ({
      xInput: slice.xInput,
      yInput: slice.yInput,
      slice: slice.pinned,
      resolution: 60,
      disabledOutputs: Array.from(disabledOutputs).sort(),
    }),
    [slice.xInput, slice.yInput, slice.pinned, disabledOutputs],
  );

  const scoreQuery = useScoreQuery(campaignId, scoreParams);

  const plotData = useMemo(() => {
    if (!scoreQuery.data) return [] as Array<Record<string, unknown>>;
    const { x, y, score, topK } = scoreQuery.data;
    const traces: Array<Record<string, unknown>> = [
      {
        type: 'heatmap',
        x,
        y,
        z: score,
        colorscale: 'RdBu',
        reversescale: true,
        zmin: 0,
        zmax: 1,
        colorbar: {
          title: { text: 'score', font: { color: '#ddd' } },
          tickfont: { color: '#ccc' },
          outlinewidth: 0,
          bgcolor: 'rgba(0,0,0,0)',
        },
        hovertemplate:
          `${slice.xInput}: %{x:.3g}<br>` +
          `${slice.yInput}: %{y:.3g}<br>` +
          'score: %{z:.3f}<extra></extra>',
      },
    ];
    if (topK && topK.length > 0) {
      traces.push({
        type: 'scatter',
        mode: 'markers+text',
        x: topK.map((p) => p.x),
        y: topK.map((p) => p.y),
        text: topK.map((p) => `${p.rank}`),
        textposition: 'top center',
        textfont: { color: '#fafafa', size: 11, family: 'Inter, sans-serif' },
        marker: {
          symbol: 'diamond',
          size: 14,
          color: '#fafafa',
          line: { color: '#0a0a0a', width: 1.5 },
        },
        hovertemplate:
          `rank %{text}<br>` +
          `${slice.xInput}: %{x:.3g}<br>` +
          `${slice.yInput}: %{y:.3g}<extra></extra>`,
        name: 'Top candidates',
        showlegend: false,
      });
    }
    return traces;
  }, [scoreQuery.data, slice.xInput, slice.yInput]);

  const layout = useMemo(
    () => ({
      autosize: true,
      margin: { l: 60, r: 20, t: 20, b: 50 },
      paper_bgcolor: '#0a0a0a',
      plot_bgcolor: '#111316',
      font: { color: '#ddd', family: 'Inter, sans-serif' },
      xaxis: {
        title: { text: slice.xInput },
        gridcolor: 'rgba(255,255,255,0.08)',
        zerolinecolor: 'rgba(255,255,255,0.15)',
      },
      yaxis: {
        title: { text: slice.yInput },
        gridcolor: 'rgba(255,255,255,0.08)',
        zerolinecolor: 'rgba(255,255,255,0.15)',
      },
    }),
    [slice.xInput, slice.yInput],
  );

  return (
    <Flex style={{ height: '100%' }}>
      {/* Sidebar: per-output toggles */}
      <Box
        style={{
          width: 240,
          flexShrink: 0,
          borderRight: '1px solid var(--gray-a5)',
          padding: '16px 14px',
          overflowY: 'auto',
        }}
      >
        <Heading size="2" style={{ marginBottom: 8 }}>
          Outputs
        </Heading>
        <Text size="1" color="gray" style={{ display: 'block', marginBottom: 12 }}>
          Uncheck to exclude from the blended score.
        </Text>
        <Flex direction="column" gap="2">
          {outputs.map((o) => {
            const enabled = !disabledOutputs.has(o.name);
            return (
              <Flex
                key={o.name}
                align="center"
                gap="2"
                style={{
                  padding: '6px 8px',
                  borderRadius: 6,
                  background: enabled ? 'var(--gray-a3)' : 'transparent',
                }}
              >
                <Checkbox
                  checked={enabled}
                  onCheckedChange={(v) => toggleOutput(o.name, Boolean(v))}
                />
                <Text size="2" style={{ color: 'var(--gray-12)' }}>
                  {o.name}
                </Text>
                <Text size="1" color="gray" style={{ marginLeft: 'auto' }}>
                  w={o.weight}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      </Box>

      {/* Main heatmap area */}
      <Flex direction="column" style={{ flex: 1, minWidth: 0 }}>
        <Box
          style={{
            padding: '10px 16px',
            borderBottom: '1px solid var(--gray-a5)',
          }}
        >
          <Text size="2" weight="medium" style={{ color: 'var(--gray-12)' }}>
            Sampler score (higher = more useful to sample)
          </Text>
          <Text size="1" color="gray" style={{ display: 'block' }}>
            Slice: {slice.xInput} x {slice.yInput}
            {Object.keys(slice.pinned).length > 0 && (
              <>
                {' '}- pinned:{' '}
                {Object.entries(slice.pinned)
                  .map(([k, v]) => `${k}=${v.toFixed(3)}`)
                  .join(', ')}
              </>
            )}
          </Text>
        </Box>

        <Box style={{ flex: 1, minHeight: 0, padding: 12 }}>
          {scoreQuery.isLoading && (
            <Text size="2" color="gray">
              Loading score field...
            </Text>
          )}
          {scoreQuery.isError && (
            <Text size="2" color="red">
              Failed to load score: {(scoreQuery.error as Error)?.message}
            </Text>
          )}
          {scoreQuery.data && (
            <Plot
              data={plotData as unknown as Plotly.Data[]}
              layout={layout as unknown as Partial<Plotly.Layout>}
              useResizeHandler
              style={{ width: '100%', height: '100%' }}
              config={{ displaylogo: false, responsive: true }}
            />
          )}
        </Box>
      </Flex>
    </Flex>
  );
}

export default SamplerTab;
